/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChronoUnit,
  DateTimeFormatter,
  DayOfWeek,
  Duration,
  Instant,
  LocalDate,
  LocalDateTime,
  LocalTime,
  TemporalAdjusters,
} from 'js-joda';
import _ from 'lodash';
import humanizeDuration from 'humanize-duration';

import * as time from '@tock/shared/utils/time';
import { IntlShape } from 'react-intl';
import { TimeslotOptionData } from 'components/reservation/reservationDateTimeOptions';
import AppSettings from 'settings/AppSettings';

type Humanizer = (durationInMilliseconds: number, options?: any) => string;

export const shortEnglishHumanizer: Humanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
});

export const RESO_TIME_DATE_FORMATTER = DateTimeFormatter.ofPattern(`yyyy-MM-dd'T'HH':'mm`);

export function adjustForBusinessDay(
  date: string | LocalDate,
  time: string | LocalTime
): LocalDateTime {
  const localDate = _.isString(date) ? fastParseDate(date) : date;
  const localTime = _.isString(time) ? fastParseTime(time) : time;

  return localDate
    .atTime(localTime)
    .plusDays(localTime.isBefore(fastParseTime(AppSettings.LAST_TIME_IN_BUSINESS_DAY)) ? 1 : 0);
}

export function getServiceDateOfDateAndTime(
  date: string | LocalDate,
  time: string | LocalTime
): LocalDate {
  const localDate = _.isString(date) ? fastParseDate(date) : date;
  const localTime = _.isString(time) ? fastParseTime(time) : time;

  return localDate.minusDays(
    localTime.isBefore(fastParseTime(AppSettings.LAST_TIME_IN_BUSINESS_DAY)) ? 1 : 0
  );
}

export function getServiceDateTime(dateTime: string | LocalDateTime): LocalDateTime {
  const localDateTime = typeof dateTime === 'string' ? fastParseDateTime(dateTime) : dateTime;
  const localTime = localDateTime.toLocalTime();

  return localDateTime.minusDays(
    localTime.isBefore(fastParseTime(AppSettings.LAST_TIME_IN_BUSINESS_DAY)) ? 1 : 0
  );
}

// We add some utility functions to LocalTime for handling before/after comparisons
// adjusted for our 4AM business day rules:
declare module 'js-joda' {
  interface LocalTime {
    isBeforeConsideringBusinessDay(otherTime: LocalTime): boolean;
    isAfterConsideringBusinessDay(otherTime: LocalTime): boolean;
  }
}

LocalTime.prototype.isBeforeConsideringBusinessDay = function (otherTime: LocalTime) {
  const nowDay = LocalDate.now();
  const thisAdjusted = adjustForBusinessDay(nowDay, this);
  return thisAdjusted.isBefore(adjustForBusinessDay(nowDay, otherTime));
};

LocalTime.prototype.isAfterConsideringBusinessDay = function (otherTime: LocalTime) {
  const nowDay = LocalDate.now();
  const thisAdjusted = adjustForBusinessDay(nowDay, this);
  return thisAdjusted.isAfter(adjustForBusinessDay(nowDay, otherTime));
};

export function adjustRangeForBusinessDay(
  date: string | LocalDate,
  start: string | LocalTime,
  end: string | LocalTime
) {
  const localDate = _.isString(date) ? fastParseDate(date) : date;
  const startTime = _.isString(start) ? fastParseTime(start) : start;
  const endTime = _.isString(end) ? fastParseTime(end) : end;

  return {
    start: adjustForBusinessDay(localDate, startTime),
    end: adjustForBusinessDay(localDate, endTime.minusMinutes(15)).plusMinutes(15),
  };
}

export function getBusinessDay(date: LocalDateTime): LocalDate {
  return date.toLocalTime().isBefore(fastParseTime(AppSettings.LAST_TIME_IN_BUSINESS_DAY))
    ? date.toLocalDate().minusDays(1)
    : date.toLocalDate();
}

export function calculateSeatedTime(seatedTimeSec: number): Duration {
  const diffFromNow = Math.max(Instant.now().epochSecond() - seatedTimeSec, 0);
  return Duration.ofSeconds(_.toInteger(diffFromNow));
}

export function formatReservationTime(reservationLocalDateTime: LocalDateTime): string {
  return RESO_TIME_DATE_FORMATTER.format(reservationLocalDateTime);
}

export function formatSeatedTime(seatedTimeSec: number): string {
  const duration = calculateSeatedTime(seatedTimeSec);
  return formatDuration(duration);
}

export function formatSeatedTimeAsHMText(seatedTimeSec: number): string {
  const duration = calculateSeatedTime(seatedTimeSec);
  return formatDurationAsHMText(duration);
}

export function formatDuration(durationParam: Duration): string {
  const isNegative = durationParam.toMinutes() < 0;
  const duration = isNegative ? durationParam.negated() : durationParam;
  const mins = duration.toMinutes() - Duration.ofHours(duration.toHours()).toMinutes();
  return `${isNegative ? '-' : ''}${duration.toHours()}:${_.padStart(_.toString(mins), 2, '0')}`;
}

export function millisToNextMinute(seatedTime?: Duration) {
  return seatedTime ? (60 - (seatedTime.seconds() % 60)) * 1000 : undefined;
}

// A more "prose" style time format. "3h 9m" instead of "3:09", for use in parts of
// sentences: e.g. "Dined for 3h 9m" instead of "Dined for 3:09".
export function formatDurationAsHMText(duration: Duration): string {
  const hours = duration.toHours();
  const minutes = duration.toMinutes() - Duration.ofHours(duration.toHours()).toMinutes();
  const timeParts: string[] = [];
  if (hours) {
    timeParts.push(`${hours}h`);
  }
  if (minutes) {
    timeParts.push(`${minutes}m`);
  }
  if (timeParts.length > 0) {
    return timeParts.join(' ');
  } else {
    return '0m';
  }
}

export function formatDurationAsMText(duration: Duration): string {
  const minutes = duration.toMinutes();
  return `${minutes}m`;
}

export function humanizeDurationString(
  durationString: string,
  chronoUnit: ChronoUnit,
  options?: object,
  humanizer?: Humanizer
) {
  const durationNumber = _.toNumber(durationString);
  return humanize(Duration.of(durationNumber, chronoUnit), options, humanizer);
}

export function humanize(
  duration: Duration,
  options?: object,
  humanizer = humanizeDuration as Humanizer
): string {
  return humanizer(duration.toMillis(), _.merge({ round: true }, options));
}

export function get15MinTimeIntervals(date: string | LocalDate): LocalDateTime[] {
  // Start at 4am
  let localDate: LocalDate;
  if (_.isString(date)) {
    localDate = fastParseDate(date);
  } else {
    localDate = date;
  }
  let startTime = localDate.atTime(LocalTime.of(4, 0));
  const endTime = startTime.plusDays(1);
  const timeIntervals: LocalDateTime[] = [];
  // We want to end before 4am on the next day
  while (startTime.isBefore(endTime)) {
    timeIntervals.push(startTime);
    startTime = startTime.plusMinutes(15);
  }

  return timeIntervals;
}

export function internationalizeTimeOptions<T>(
  partyReservationTimeOptions: T[],
  intl: IntlShape
): T[] {
  return _.map(partyReservationTimeOptions as any[], (option) => {
    if (!option.localDateTime) {
      return option;
    }

    return _.assign({}, option, {
      label: time.formatTime(intl, option.localDateTime, {
        timeZone: 'UTC',
        hour: 'numeric',
        minute: '2-digit',
      }),
    });
  });
}

export function isInCorrectDateFormat(dateString: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString) && isValidDateString(dateString);
}

export function isValidDateTimeString(dateTimeString: string): boolean {
  return !!parseIfIsValidDateTimeString(dateTimeString);
}

export function isValidDateString(dateString: string): boolean {
  try {
    LocalDate.parse(dateString);
  } catch (_error) {
    return false;
  }
  return true;
}

export function parseIfIsValidDateTimeString(dateTimeString: string): LocalDateTime | null {
  let localDateTime: LocalDateTime | null = null;
  try {
    localDateTime = fastParseDateTime(dateTimeString);
  } catch (_error) {
    // Pass
  }
  return localDateTime;
}

/**
 * Returns the first day of a calendar month (starting on Sunday) given a date.
 * This date will often be from the previous month, depending on what day of the
 * week the month of the date parameter begins.
 * @param {Object} date A LocalDate to center the calendar around.
 * @returns {Object} First date of the calendar month of the date parameter.
 */
export function getFirstDateOfCalendarMonth(date: LocalDate): LocalDate {
  const firstDate = date.with(TemporalAdjusters.firstDayOfMonth());
  const dayOfWeek = firstDate.dayOfWeek();
  return dayOfWeek.equals(DayOfWeek.SUNDAY) ? firstDate : firstDate.minusDays(dayOfWeek.value());
}

/**
 * Returns the last day of a calendar month (starting on Sunday) given a date.
 * This date will often be from the next month, depending on what day of the
 * week the month of the date parameter ends.
 * @param {Object} date A LocalDate to center the calendar around.
 * @returns {Object} Last date of the calendar month of the date parameter.
 */
export function getLastDateOfCalendarMonth(date: LocalDate): LocalDate {
  let lastDate = date.with(TemporalAdjusters.lastDayOfMonth());
  const dayOfWeek = lastDate.dayOfWeek();
  lastDate =
    dayOfWeek === DayOfWeek.SUNDAY
      ? lastDate.plusDays(6)
      : lastDate.plusDays(6 - dayOfWeek.value());

  return lastDate;
}

export function getNumDaysInCalendarMonth(firstDate: LocalDate, lastDate: LocalDate): number {
  return firstDate.until(lastDate, ChronoUnit.DAYS) + 1;
}

export function getDateRangeOfCalendarMonth(date: LocalDate): string[] {
  const firstDate = getFirstDateOfCalendarMonth(date);
  const lastDate = getLastDateOfCalendarMonth(date);
  const numDaysToDisplay = getNumDaysInCalendarMonth(firstDate, lastDate);

  const dates = [] as string[];
  let cursor = firstDate;

  for (let i = 0; i < numDaysToDisplay; ++i) {
    const dateString = cursor.toString();
    dates.push(dateString);
    cursor = cursor.plusDays(1);
  }

  return dates;
}

export function roundTo15MinInterval(dateTime: LocalDateTime | string): LocalDateTime {
  let timeToRound;
  if (_.isEmpty(dateTime)) {
    timeToRound = LocalDateTime.now();
  } else {
    timeToRound = typeof dateTime === 'string' ? fastParseDateTime(dateTime) : dateTime;
  }

  return timeToRound
    .minusMinutes(timeToRound.minute() % 15)
    .withSecond(0)
    .withNano(0);
}

export function truncateTo15Min(dateTime: LocalDateTime): LocalDateTime {
  return dateTime
    .minusMinutes(dateTime.minute() % 15)
    .withSecond(0)
    .withNano(0);
}

const dateTimeCache: { [key: string]: LocalDate | LocalDateTime | LocalTime } = {};

export function fastParseDateTime(key: string): LocalDateTime {
  let value = dateTimeCache[key];
  if (value) {
    return value as LocalDateTime;
  }

  value = LocalDateTime.parse(key);
  dateTimeCache[key] = value;
  return value;
}

export function fastParseDate(key: string): LocalDate {
  let value = dateTimeCache[key];
  if (value) {
    return value as LocalDate;
  }

  value = LocalDate.parse(key);
  dateTimeCache[key] = value;
  return value;
}
export function fastParseTime(key: string): LocalTime {
  let value = dateTimeCache[key];
  if (value) {
    return value as LocalTime;
  }

  value = LocalTime.parse(key);
  dateTimeCache[key] = value;
  return value;
}

/**
 * Checks dateTime is within the start/end range.
 * Inclusive on the start and exclusive on the end.
 * End is optional.
 */
export function isDateTimeInRange(
  dateTime: LocalDateTime,
  startDateTime: LocalDateTime,
  endDateTime?: LocalDateTime
): boolean {
  return !endDateTime
    ? dateTime.equals(startDateTime)
    : !dateTime.isBefore(startDateTime) && dateTime.isBefore(endDateTime);
}

/**
 * Checks time is within the start/end range.
 * Inclusive on the start and exclusive on the end.
 * End is optional.
 */
export function isTimeInBusinessDayRange(
  time: LocalTime,
  startTime: LocalTime,
  endTime?: LocalTime
): boolean {
  return !endTime
    ? time.equals(startTime)
    : !time.isBeforeConsideringBusinessDay(startTime) &&
        time.isBeforeConsideringBusinessDay(endTime);
}

export function sortItemsByTimeStringWithinBusinessDay<Item>(
  items: Item[],
  timeGetter: (i: Item) => string
) {
  return sortItemsByLocalTimeWithinBusinessDay(items, (item) => {
    return fastParseTime(timeGetter(item));
  });
}

export function sortItemsByLocalTimeWithinBusinessDay<Item>(
  items: Item[],
  timeGetter: (i: Item) => LocalTime | undefined
) {
  return items.sort((firstItem, secondItem) => {
    const firstItemLocalTime = timeGetter(firstItem);
    const secondItemLocalTime = timeGetter(secondItem);
    if (!firstItemLocalTime) {
      return -1;
    } else if (!secondItemLocalTime) {
      return 1;
    }
    const firstItemAdjustedLocalTime = adjustForBusinessDay(LocalDate.now(), firstItemLocalTime);
    const secondItemAdjustedLocalTime = adjustForBusinessDay(LocalDate.now(), secondItemLocalTime);
    return firstItemAdjustedLocalTime.isBefore(secondItemAdjustedLocalTime) ? -1 : 1;
  });
}

export function sortByLocalDateTime(timeOptions: SelectOptionWithData<TimeslotOptionData>[]) {
  return timeOptions.sort((a, b) => {
    const localDateTimeA = LocalDateTime.parse(a.value.toString());
    const localDateTimeB = LocalDateTime.parse(b.value.toString());

    if (localDateTimeA.isBefore(localDateTimeB)) {
      return -1;
    } else if (localDateTimeA.isEqual(localDateTimeB)) {
      return 0;
    } else {
      return 1;
    }
  });
}

export const FAKE_BUSINESS_DAY = LocalDate.of(2019, 1, 1);

/**
 * Returns the given time on an arbitrarily chosen date such that if the given
 * time represents a later time in service then the returned localdatetime will
 * be greater. (This isn't true of the input LocalTime objects; for instance
 * 11:45PM > 2:30AM but businessTime(11:45PM) < businessTime(2:30AM).
 */
export function businessTime(time: LocalTime): LocalDateTime {
  if (time.isBefore(LocalTime.of(4, 0))) {
    return FAKE_BUSINESS_DAY.plusDays(1).atTime(time);
  } else {
    return FAKE_BUSINESS_DAY.atTime(time);
  }
}
